import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { formatDate as formatUserDate, formatTime as formatUserTime } from '../../utils/dateTimeFormat';

const STATUS_STYLES = {
  PASSED: { badge: 'bg-green-50 text-green-700 ring-green-600/20', dot: 'bg-green-500' },
  FAILED: { badge: 'bg-red-50 text-red-700 ring-red-600/20', dot: 'bg-red-500' },
  RUNNING: { badge: 'bg-blue-50 text-blue-700 ring-blue-600/20', dot: 'bg-blue-500' },
  PENDING: { badge: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20', dot: 'bg-yellow-500' },
  SKIPPED: { badge: 'bg-gray-50 text-gray-600 ring-gray-500/20', dot: 'bg-gray-400' },
  ABORTED: { badge: 'bg-orange-50 text-orange-700 ring-orange-600/20', dot: 'bg-orange-500' },
};

const DEFAULT_STATUS_STYLE = { badge: 'bg-gray-50 text-gray-600 ring-gray-500/20', dot: 'bg-gray-400' };

const ENTITY_CONFIG = {
  suite: {
    idKey: 'suite_id',
    nameKey: 'suite_name',
    endpoint: '/test_case/get_test_suite_execution',
    paramKey: 'suite_id',
    executionIdKey: 'suite_execution_id',
    label: 'test suite',
  },
  plan: {
    idKey: 'test_plan_id',
    nameKey: 'name',
    endpoint: '/test_case/get_test_plan_execution',
    paramKey: 'test_plan_id',
    executionIdKey: 'test_plan_execution_id',
    label: 'test plan',
  },
};

const formatDate = (date) => formatUserDate(date, '-');

const formatTime = (date) => formatUserTime(date);

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds)) return '-';
  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const remaining = Math.round(totalSeconds % 60);
  return `${minutes}m ${remaining}s`;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const passRateBarColor = (rate) => {
  if (rate >= 90) return 'bg-green-500';
  if (rate >= 70) return 'bg-yellow-500';
  return 'bg-red-500';
};

const parseHistoryList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const renderStatusBadge = (status) => {
  const normalized = String(status || '').toUpperCase();
  const style = STATUS_STYLES[normalized] || DEFAULT_STATUS_STYLE;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${style.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
      {normalized || '-'}
    </span>
  );
};

const ChevronIcon = ({ expanded }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

// Sub-component: Test case rows for a suite execution
const TestCaseAccordion = ({ suiteExecutionId }) => {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const res = await axiosInstance.get('/test_case/get_test_suite_case_execution', {
          params: { suite_execution_id: suiteExecutionId },
        });
        if (!active) return;
        const data = res;
        setTestCases(data?.test_cases || []);
      } catch (err) {
        if (!active) return;
        setError('Failed to load test cases.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetch();
    return () => { active = false; };
  }, [suiteExecutionId]);

  if (loading) {
    return (
      <tr>
        <td colSpan="100%" className="px-8 py-3">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            Loading test cases...
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan="100%" className="px-8 py-3 text-xs text-red-500">{error}</td>
      </tr>
    );
  }

  if (testCases.length === 0) {
    return (
      <tr>
        <td colSpan="100%" className="px-8 py-3 text-xs text-gray-400">No test cases found.</td>
      </tr>
    );
  }

  return (
    <>
      <tr className="bg-gray-50/50">
        <td colSpan="100%" className="px-0 py-0">
          <div className="ml-10 mr-6 my-2 border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 text-left font-semibold text-gray-500">Test Case</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-500">Environment</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-500">Duration</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-500">Executed At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {testCases.map((tc) => (
                  <tr key={tc.test_case_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-700 font-medium">{tc.test_case_name}</td>
                    <td className="px-4 py-2">{renderStatusBadge(tc.status)}</td>
                    <td className="px-4 py-2 text-gray-600">{tc.environment_name || '-'}</td>
                    <td className="px-4 py-2 text-gray-600 tabular-nums">{formatDuration(tc.duration)}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {formatDate(tc.created_at)} · {formatTime(tc.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    </>
  );
};

// Sub-component: Suite execution rows for a plan execution (with nested test case accordion)
const SuiteExecutionAccordion = ({ testPlanExecutionId }) => {
  const [suiteExecutions, setSuiteExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSuites, setExpandedSuites] = useState({});

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const res = await axiosInstance.get('/test_case/get_test_plan_suite_execution', {
          params: { test_plan_execution_id: testPlanExecutionId },
        });
        if (!active) return;
        const data = res;
        setSuiteExecutions(data?.suite_executions || []);
      } catch (err) {
        if (!active) return;
        setError('Failed to load suite executions.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetch();
    return () => { active = false; };
  }, [testPlanExecutionId]);

  const toggleSuite = (suiteExecId) => {
    setExpandedSuites((prev) => ({ ...prev, [suiteExecId]: !prev[suiteExecId] }));
  };

  if (loading) {
    return (
      <tr>
        <td colSpan="100%" className="px-8 py-3">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            Loading suite executions...
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan="100%" className="px-8 py-3 text-xs text-red-500">{error}</td>
      </tr>
    );
  }

  if (suiteExecutions.length === 0) {
    return (
      <tr>
        <td colSpan="100%" className="px-8 py-3 text-xs text-gray-400">No suite executions found.</td>
      </tr>
    );
  }

  return (
    <tr className="bg-gray-50/50">
      <td colSpan="100%" className="px-0 py-0">
        <div className="ml-10 mr-6 my-2 border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-100">
                <th className="w-8 px-2 py-2"></th>
                <th className="px-4 py-2 text-left font-semibold text-gray-500">Suite Name</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-500">Status</th>
                <th className="px-4 py-2 text-center font-semibold text-gray-500">Results</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-500">Pass Rate</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-500">Duration</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-500">Executed At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {suiteExecutions.map((se) => {
                const passRate = Number(se.pass_rate) || 0;
                const isExpanded = !!expandedSuites[se.suite_execution_id];
                return (
                  <React.Fragment key={se.suite_execution_id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleSuite(se.suite_execution_id)}
                    >
                      <td className="px-2 py-2 text-gray-400">
                        <ChevronIcon expanded={isExpanded} />
                      </td>
                      <td className="px-4 py-2 text-gray-700 font-medium">{se.suite_name}</td>
                      <td className="px-4 py-2">{renderStatusBadge(se.execution_status)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className="font-semibold text-green-600">{se.passed ?? 0}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className={`font-semibold ${(se.failed ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {se.failed ?? 0}
                        </span>
                        <span className="text-gray-400 ml-1">of {se.total_test_cases ?? 0}</span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${passRateBarColor(passRate)}`}
                              style={{ width: `${Math.min(Math.max(passRate, 0), 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-gray-700 font-medium tabular-nums">{passRate}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-600 tabular-nums">{formatDuration(se.duration)}</td>
                      <td className="px-4 py-2 text-gray-500">
                        {formatDate(se.created_at)} · {formatTime(se.created_at)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="100%" className="p-0">
                          <div className="ml-8">
                            <table className="w-full">
                              <tbody>
                                <TestCaseAccordion suiteExecutionId={se.suite_execution_id} />
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
};

/**
 * Shared execution history modal for test suites and test plans.
 * @param {'suite'|'plan'} entityType
 * @param {object|null} entity - suite or plan object matching entityType
 */
const ExecutionHistoryModal = ({ isOpen, onClose, entityType, entity }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const config = ENTITY_CONFIG[entityType];
  const entityId = config && entity ? entity[config.idKey] : null;
  const entityName = config && entity ? entity[config.nameKey] : null;

  useEffect(() => {
    const activeConfig = ENTITY_CONFIG[entityType];
    if (!isOpen || !activeConfig || !entityId) return;

    let isActive = true;

    const fetchExecutionHistory = async () => {
      setLoading(true);
      setError(null);
      setHistory([]);
      setExpandedRows({});
      try {
        const res = await axiosInstance.get(activeConfig.endpoint, {
          params: { [activeConfig.paramKey]: entityId },
        });
        if (!isActive) return;
        setHistory(parseHistoryList(res?.data));
      } catch (err) {
        console.error(`Error fetching ${activeConfig.label} execution history:`, err);
        if (!isActive) return;
        setError('Failed to load execution history. Please try again.');
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchExecutionHistory();

    return () => {
      isActive = false;
    };
  }, [isOpen, entityType, entityId]);

  const toggleRow = (executionId) => {
    setExpandedRows((prev) => ({ ...prev, [executionId]: !prev[executionId] }));
  };

  if (!isOpen || !config) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[2000]"
        onClick={onClose}
      ></div>
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2001] w-[92vw] max-w-5xl max-h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 bg-[#0D6EFD]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="m-0 text-lg font-semibold text-white leading-tight">Execution History</h2>
              {entityName && (
                <p className="m-0 text-sm text-blue-100 truncate">{entityName}</p>
              )}
            </div>
          </div>
          <button
            className="flex-shrink-0 bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/10 cursor-pointer p-1.5 rounded-lg transition-colors"
            onClick={onClose}
            aria-label="Close execution history"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <div className="w-8 h-8 border-[3px] border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
              <p className="m-0 text-sm text-gray-500">Loading execution history...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 px-6">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <p className="m-0 text-sm text-gray-600 text-center">{error}</p>
            </div>
          ) : history.length > 0 ? (
            <div className="max-h-[55vh] overflow-y-auto">
              <table className="w-full border-separate border-spacing-0 text-sm table-fixed">
                <thead>
                  <tr>
                    <th className="w-[5%] sticky top-0 z-10 bg-gray-50 pl-4 pr-1 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200"></th>
                    <th className="w-[20%] sticky top-0 z-10 bg-gray-50 pl-2 pr-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Execution</th>
                    <th className="w-[18%] sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Executed By</th>
                    <th className="w-[14%] sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Status</th>
                    <th className="w-[17%] sticky top-0 z-10 bg-gray-50 px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Results</th>
                    <th className="w-[16%] sticky top-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Pass Rate</th>
                    <th className="w-[10%] sticky top-0 z-10 bg-gray-50 pl-3 pr-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((execution) => {
                    const passRate = Number(execution.pass_rate) || 0;
                    const executionId = execution[config.executionIdKey];
                    const isExpanded = !!expandedRows[executionId];
                    return (
                      <React.Fragment key={executionId}>
                        <tr
                          className="hover:bg-gray-50/70 transition-colors cursor-pointer"
                          onClick={() => toggleRow(executionId)}
                        >
                          <td className="pl-4 pr-1 py-3.5 align-middle text-gray-400">
                            <ChevronIcon expanded={isExpanded} />
                          </td>
                          <td className="pl-2 pr-3 py-3.5 align-middle">
                            <div className="text-xs text-gray-500 mt-0.5">
                              {formatDate(execution.created_at)} · {formatTime(execution.created_at)}
                            </div>
                          </td>
                          <td className="px-3 py-3.5 align-middle">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold flex items-center justify-center">
                                {getInitials(execution.executed_by_name)}
                              </div>
                              <span className="text-gray-700 truncate">{execution.executed_by_name || '-'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3.5 align-middle">
                            {renderStatusBadge(execution.execution_status)}
                          </td>
                          <td className="px-3 py-3.5 align-middle text-center">
                            <span className="font-semibold text-green-600">{execution.passed ?? 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className={`font-semibold ${(execution.failed ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {execution.failed ?? 0}
                            </span>
                            <span className="text-xs text-gray-400 ml-1.5">of {execution.total_test_cases ?? 0}</span>
                          </td>
                          <td className="px-3 py-3.5 align-middle">
                            <div className="flex items-center gap-2.5">
                              <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${passRateBarColor(passRate)}`}
                                  style={{ width: `${Math.min(Math.max(passRate, 0), 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-gray-700 font-medium tabular-nums">{passRate}%</span>
                            </div>
                          </td>
                          <td className="pl-3 pr-6 py-3.5 align-middle text-right text-gray-600 tabular-nums">
                            {formatDuration(execution.duration)}
                          </td>
                        </tr>
                        {isExpanded && entityType === 'suite' && (
                          <TestCaseAccordion suiteExecutionId={executionId} />
                        )}
                        {isExpanded && entityType === 'plan' && (
                          <SuiteExecutionAccordion testPlanExecutionId={executionId} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 px-6">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className="text-center">
                <p className="m-0 text-sm font-medium text-gray-700">No executions yet</p>
                <p className="m-0 mt-1 text-xs text-gray-500">
                  This {config.label} hasn't been executed. Run it to see its history here.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3.5 bg-gray-50 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            {loading
              ? 'Loading...'
              : `${history.length} execution${history.length === 1 ? '' : 's'}`}
          </span>
          <button
            className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default ExecutionHistoryModal;
